import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useProductDetailModal } from "@/features/product-detail/hooks/use-product-detail-modal";
import { singleInstantSearch } from "@/features/instantsearch/services/single-instantsearch";
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import type { TypesenseProductDocument } from "@/features/instantsearch/types/instantsearch-types";
import { formatPrice } from "@/features/orders/utils/format-price";
import { isModifiedClick } from "@/lib/link-utils";
import { useCommercePaths } from "@/modules";
import useAuthStore from "@/store/use-auth-store";
import { alignAttributes, cheaperIndex, type ComparedProduct } from "../model";
import { useComparisonStore } from "../store/comparison-store";

function toCompared(
  product: TypesenseProductDocument,
  fallbackId: string,
): ComparedProduct {
  const attributes = (product.attributes ?? [])
    .map((attribute) => ({
      name: attribute.name ?? "",
      value: attribute.value ?? "",
    }))
    .filter((attribute) => attribute.name !== "" && attribute.value !== "");
  return {
    productId: product.productId || fallbackId,
    offerId: product.offerId || "",
    name: product.displayName || product.name || "",
    brand: product.brand?.name || product.merchant?.name || "",
    seller: product.seller?.name || "",
    imageUrl: product.imageUrl || product.images?.[0]?.imageUrl || "",
    price: product.effectivePrice || 0,
    listPrice: product.prices?.listPrice?.value || 0,
    inStock: product.inStock === true,
    attributes,
  };
}

function slotProducts(
  documents: TypesenseProductDocument[],
  ids: readonly string[],
): (ComparedProduct | null)[] {
  return ids.map((id) => {
    const matches = documents.filter((document) => document.productId === id);
    const chosen = matches.find((document) => document.inStock) ?? matches[0];
    return chosen ? toCompared(chosen, id) : null;
  });
}

/**
 * Karşılaştırma ekranı. Sohbet yalnızca butonu çizer; ürünler burada
 * Typesense'den çekilip yan yana dizilir.
 */
export const ComparisonModal: React.FC = () => {
  const { t } = useTranslation();
  // UI store language name ("Turkish", ...), like the rest of the catalog readers;
  // i18n.language is an ISO code and never matched the name-keyed map.
  const language = useUIPreferencesStore((state) => state.language);
  const paths = useCommercePaths();
  const { openModal } = useProductDetailModal();
  const currency = useAuthStore((state) => state.user?.currency || "TRY");
  const open = useComparisonStore((state) => state.open);
  const productIds = useComparisonStore((state) => state.productIds);
  const close = useComparisonStore((state) => state.close);
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [products, setProducts] = useState<(ComparedProduct | null)[]>([]);

  useEffect(() => {
    if (!open || productIds.length < 2) return;
    const controller = new AbortController();
    setStatus("loading");
    singleInstantSearch<TypesenseProductDocument>({
      productIds: [...productIds],
      perPage: productIds.length,
      language,
      signal: controller.signal,
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        setProducts(slotProducts(result.hits, productIds));
        setStatus("ready");
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus("error");
      });
    return () => controller.abort();
  }, [open, productIds, language, attempt]);

  const rows = useMemo(
    () =>
      alignAttributes(
        products.map((product) => ({ attributes: product?.attributes ?? [] })),
      ),
    [products],
  );

  if (!open) return null;

  const winner = cheaperIndex(products.map((product) => product?.price ?? 0));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        open={open}
        className="flex max-h-[min(92dvh,880px)] w-full max-w-3xl flex-col overflow-hidden p-0"
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-border-secondary px-4 py-3 dark:border-gray-700 sm:px-6">
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-base font-semibold text-text-primary dark:text-gray-100">
              {t("comparison.title")}
            </DialogTitle>
            <p className="text-xs text-text-tertiary dark:text-gray-400">
              {t("comparison.count", { count: productIds.length })}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t("comparison.close")}
            className="flex size-9 items-center justify-center rounded-full text-text-tertiary outline-none hover:bg-bg-secondary focus-visible:ring-2 focus-visible:ring-module-accent dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 pb-8 sm:px-6">
          {status === "loading" && <SheetSkeleton count={productIds.length} />}
          {status === "error" && (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-text-secondary dark:text-gray-300">
                {t("comparison.error")}
              </p>
              <button
                type="button"
                onClick={() => setAttempt((value) => value + 1)}
                className="rounded-xl bg-module-accent px-4 py-2 text-sm font-medium text-module-accent-foreground hover:bg-module-accent-hover"
              >
                {t("comparison.retry")}
              </button>
            </div>
          )}
          {status === "ready" && (
            <>
              <div
                className="grid gap-3 sm:gap-5"
                style={{
                  gridTemplateColumns: `repeat(${String(products.length)}, minmax(0, 1fr))`,
                }}
              >
                {products.map((product, index) => (
                  <ProductColumn
                    key={product?.productId ?? `missing-${String(index)}`}
                    product={product}
                    cheaper={winner === index}
                    currency={currency}
                    href={
                      product
                        ? paths.product(product.productId, {
                            offerId: product.offerId || undefined,
                          })
                        : ""
                    }
                    onOpen={() => {
                      if (!product) return;
                      close();
                      openModal(
                        product.productId,
                        product.offerId || undefined,
                      );
                    }}
                  />
                ))}
              </div>
              {rows.length > 0 && (
                <section className="mt-5">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-tertiary dark:text-gray-400">
                    {t("comparison.specs")}
                  </h3>
                  <div className="overflow-hidden rounded-2xl border border-border-secondary dark:border-gray-700">
                    {rows.map((row) => {
                      const filled = row.values.filter((value) => value !== "");
                      const differs = new Set(filled).size > 1;
                      return (
                        <div
                          key={row.name}
                          className="grid border-b border-border-secondary last:border-b-0 dark:border-gray-700"
                          style={{
                            gridTemplateColumns: `minmax(5.5rem, 0.8fr) repeat(${String(row.values.length)}, minmax(0, 1fr))`,
                          }}
                        >
                          <div className="min-w-0 break-words bg-bg-secondary px-3 py-3 text-xs font-medium text-text-tertiary dark:bg-gray-800/60 dark:text-gray-400">
                            {row.name}
                          </div>
                          {row.values.map((value, index) => (
                            <div
                              key={`${row.name}-${String(index)}`}
                              className={
                                differs
                                  ? "min-w-0 break-words bg-module-accent-soft px-3 py-3 text-sm font-medium text-text-primary dark:text-gray-100"
                                  : "min-w-0 break-words px-3 py-3 text-sm text-text-secondary dark:text-gray-300"
                              }
                            >
                              {value === "" ? "—" : value}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function ProductColumn({
  product,
  cheaper,
  currency,
  href,
  onOpen,
}: {
  product: ComparedProduct | null;
  cheaper: boolean;
  currency: string;
  href: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  if (!product) {
    return (
      <div className="flex aspect-[3/4] items-center justify-center rounded-2xl border border-dashed border-border-secondary text-center text-sm text-text-tertiary dark:border-gray-700 dark:text-gray-400">
        {t("comparison.missing")}
      </div>
    );
  }
  const discounted = product.listPrice > product.price && product.price > 0;
  return (
    <div className="flex min-w-0 flex-col">
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-bg-secondary dark:bg-gray-800">
        {product.imageUrl !== "" && (
          <img
            src={product.imageUrl}
            alt=""
            className="size-full object-cover"
          />
        )}
        {cheaper && (
          <span className="absolute start-2 top-2 rounded-full bg-module-accent px-2 py-0.5 text-[11px] font-semibold text-module-accent-foreground">
            {t("comparison.cheaper")}
          </span>
        )}
      </div>
      {product.brand !== "" && (
        <p className="mt-3 truncate text-[11px] font-medium uppercase tracking-wide text-text-tertiary dark:text-gray-400">
          {product.brand}
        </p>
      )}
      <p className="mt-0.5 line-clamp-2 text-sm font-semibold text-text-primary dark:text-gray-100">
        {product.name}
      </p>
      {product.seller !== "" && (
        <p className="mt-0.5 truncate text-xs text-text-tertiary dark:text-gray-400">
          {product.seller}
        </p>
      )}
      <p className="mt-1 text-base font-semibold text-module-accent">
        {formatPrice(product.price, currency)}
      </p>
      {discounted && (
        <p className="text-xs text-text-tertiary line-through dark:text-gray-500">
          {formatPrice(product.listPrice, currency)}
        </p>
      )}
      <p
        className={
          product.inStock
            ? "mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"
            : "mt-1 text-xs font-medium text-red-600 dark:text-red-400"
        }
      >
        {product.inStock ? t("comparison.inStock") : t("comparison.outOfStock")}
      </p>
      <Link
        to={href}
        onClick={(event) => {
          if (isModifiedClick(event)) return;
          event.preventDefault();
          onOpen();
        }}
        className="mt-3 block rounded-xl bg-module-accent px-3 py-2 text-center text-sm font-medium text-module-accent-foreground hover:bg-module-accent-hover"
      >
        {t("comparison.view")}
      </Link>
    </div>
  );
}

function SheetSkeleton({ count }: { count: number }) {
  return (
    <div
      className="grid gap-3 sm:gap-5"
      style={{
        gridTemplateColumns: `repeat(${String(count)}, minmax(0, 1fr))`,
      }}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="animate-pulse">
          <div className="aspect-[3/4] rounded-2xl bg-bg-secondary dark:bg-gray-800" />
          <div className="mt-3 h-3 w-2/3 rounded bg-bg-secondary dark:bg-gray-800" />
          <div className="mt-2 h-3 w-full rounded bg-bg-secondary dark:bg-gray-800" />
          <div className="mt-3 h-9 rounded-xl bg-bg-secondary dark:bg-gray-800" />
        </div>
      ))}
    </div>
  );
}
